import { PUZZLE_LEVEL_COUNT } from './puzzleMeta'
import { ACHIEVEMENTS, ACHIEVEMENT_BY_ID } from './achievements'
/**
 * Leaderboard contract shared by the browser and the server API
 * (v1.18): the client submits a progress CLAIM; the server verifies
 * identity, validates the claim, and computes the score itself. One
 * definition here so they can never drift apart.
 */
export interface ProgressClaim {
  ascension: number
  act: number
  nodesCleared: number
  victory: boolean
  playtimeMs: number
  /** ⏱️ v5.23: lets the server match the run to its start stamp and
   * clamp the claimed play-time to real wall-clock. Optional — old
   * clients and unstamped runs submit without one. */
  runId?: string
}

/**
 * ⏱️ v5.23 (per Javi: "I did a run faster and it did not update"):
 * does a new claim beat the stored row? A higher score always wins;
 * an EQUAL score falls to the stopwatch — the faster run takes the
 * row. Stored rows without a time lose ties to any timed run.
 */
export function beatsStored(
  stored: { score?: number; playtimeMs?: number } | null | undefined,
  score: number,
  playtimeMs: number,
): boolean {
  if (!stored) return true
  const prev = stored.score ?? 0
  if (score !== prev) return score > prev
  return playtimeMs < (stored.playtimeMs ?? Number.POSITIVE_INFINITY)
}

/** Farthest-progress score. Victory dominates partial progress at the
 * same ascension; ascension dominates everything (you only play A(n+1)
 * by beating A(n)). Max possible ≈ 10.5M. */
export function progressScore(s: ProgressClaim): number {
  return s.ascension * 1_000_000
    + (s.victory ? 500_000 : s.act * 10_000 + s.nodesCleared * 10)
}

/**
 * 🧩 Puzzle Game claim (v1.28): highest gated level completed plus the
 * summed best damage across completed levels. Score, per Javi's design:
 * 25 − highestLevel + damageTotal — 0 is a perfect full clear, LOWEST
 * wins (the reverse of the expedition board).
 */
export interface PuzzleClaim {
  highestLevel: number
  damageTotal: number
}

export function puzzleScore(c: PuzzleClaim): number {
  return PUZZLE_LEVEL_COUNT - c.highestLevel + c.damageTotal
}

export function puzzleClaimError(c: unknown): string | null {
  if (!c || typeof c !== 'object') return 'no claim'
  const d = c as Record<string, unknown>
  const intIn = (v: unknown, lo: number, hi: number) =>
    typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi
  if (!intIn(d.highestLevel, 0, PUZZLE_LEVEL_COUNT)) return 'highestLevel out of range'
  if (!intIn(d.damageTotal, 0, 10_000)) return 'damageTotal out of range'
  return null
}

/**
 * ⚔️ Gauntlet claim (v2.34): infinite acts past Ascension 10 — ranked
 * by DEEPEST ACT, nodes cleared breaking ties (client sorts equal
 * scores by playtime). Act dominates: nodes contribute < 1M per act.
 */
export interface GauntletClaim {
  act: number
  nodesCleared: number
  playtimeMs: number
  /** 🛡️ v6.42: lets the server match the run to its start stamp. */
  runId?: string
}

export function gauntletScore(c: GauntletClaim): number {
  return c.act * 1_000_000 + c.nodesCleared * 10
}

export function gauntletClaimError(c: unknown): string | null {
  if (!c || typeof c !== 'object') return 'no claim'
  const d = c as Record<string, unknown>
  const intIn = (v: unknown, lo: number, hi: number) =>
    typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi
  if (!intIn(d.act, 1, 500)) return 'act out of range'
  if (!intIn(d.nodesCleared, 0, 20_000)) return 'nodesCleared out of range'
  if (typeof d.playtimeMs !== 'number' || !Number.isFinite(d.playtimeMs)
    || d.playtimeMs < 0 || d.playtimeMs > 1_000 * 60 * 60 * 24 * 30) {
    return 'playtimeMs out of range'
  }
  if (d.runId !== undefined && (typeof d.runId !== 'string' || !/^[A-Za-z0-9_-]{4,64}$/.test(d.runId))) {
    return 'runId malformed'
  }
  return null
}

/**
 * 🎲 Daily claim (v5.54, designed with Javi): everyone plays the same
 * seeded run, so the board is keyed by the UTC day and ascension is not
 * a score dimension (it's the same for all). Victory dominates depth;
 * playtime breaks ties via beatsStored. Unlimited attempts — the row
 * keeps the best.
 */
export interface DailyClaim {
  day: string
  act: number
  nodesCleared: number
  victory: boolean
  playtimeMs: number
  runId?: string
}

export function dailyScore(c: Pick<DailyClaim, 'act' | 'nodesCleared' | 'victory'>): number {
  return c.victory ? 500_000 : c.act * 10_000 + c.nodesCleared * 10
}

export const DAILY_DAY_RE = /^\d{4}-\d{2}-\d{2}$/

/** 🥇 The Weekly Expedition podium's Stardust, by final rank (paid by /api/daily/claim-top3 once the week has
 * closed). v7.79: shared, so the Weekly board shows the same numbers beside the ranks (per Javi). */
export const WEEKLY_PODIUM_STARDUST: Readonly<Record<number, number>> = { 1: 500, 2: 300, 3: 150 }

/** The Stardust a final Weekly rank pays (0 below the podium). */
export function weeklyPodiumStardust(rank: number): number {
  return WEEKLY_PODIUM_STARDUST[rank] ?? 0
}

/** 🗓️ v5.63 (per Javi): the shared-seed expedition turned WEEKLY — one
 * seed per UTC week. The key is the week's MONDAY as YYYY-MM-DD; server
 * routes and the client derive it from the same function so the board
 * doc, the seed and the claim can never disagree. (The app has a local
 * twin in utils/seededRandom — pinned equal in tests — because test
 * bundles alias only `~`.) */
export function weeklyDateKey(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}

/** 🗓️ v7.80: one Weekly week. */
export const WEEK_MS = 7 * 86_400_000

/** 🗓️ v7.80 (per Javi: "post when it resets"): the instant this Weekly week ends and the next begins - the coming
 * Monday 00:00 UTC. At exactly that instant the new week has begun, so it answers the Monday after (the same sum as
 * the claim route's weekEndMs). Sunday evening in the Americas, Monday in Europe and Asia. */
export function weeklyResetAt(now: Date | number = Date.now()): number {
  return Date.parse(`${weeklyDateKey(new Date(now))}T00:00:00Z`) + WEEK_MS
}

export function dailyClaimError(c: unknown): string | null {
  if (!c || typeof c !== 'object') return 'no claim'
  const d = c as Record<string, unknown>
  const intIn = (v: unknown, lo: number, hi: number) =>
    typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi
  if (typeof d.day !== 'string' || !DAILY_DAY_RE.test(d.day)) return 'day malformed'
  if (!intIn(d.act, 1, 3)) return 'act out of range'
  if (!intIn(d.nodesCleared, 0, 100)) return 'nodesCleared out of range'
  if (typeof d.victory !== 'boolean') return 'victory not boolean'
  if (typeof d.playtimeMs !== 'number' || !Number.isFinite(d.playtimeMs)
    || d.playtimeMs < 0 || d.playtimeMs > 1_000 * 60 * 60 * 24 * 30) {
    return 'playtimeMs out of range'
  }
  if (d.runId !== undefined && (typeof d.runId !== 'string' || !/^[A-Za-z0-9_-]{4,64}$/.test(d.runId))) {
    return 'runId malformed'
  }
  return null
}

/** Returns an error string for a malformed/impossible claim, or null. */
export function claimError(c: unknown): string | null {
  if (!c || typeof c !== 'object') return 'no claim'
  const d = c as Record<string, unknown>
  const intIn = (v: unknown, lo: number, hi: number) =>
    typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi
  if (!intIn(d.ascension, 0, 10)) return 'ascension out of range'
  if (!intIn(d.act, 1, 3)) return 'act out of range'
  if (!intIn(d.nodesCleared, 0, 100)) return 'nodesCleared out of range'
  if (typeof d.victory !== 'boolean') return 'victory not boolean'
  if (typeof d.playtimeMs !== 'number' || !Number.isFinite(d.playtimeMs)
    || d.playtimeMs < 0 || d.playtimeMs > 1_000 * 60 * 60 * 24 * 30) {
    return 'playtimeMs out of range'
  }
  if (d.runId !== undefined && (typeof d.runId !== 'string' || !/^[A-Za-z0-9_-]{4,64}$/.test(d.runId))) {
    return 'runId malformed'
  }
  return null
}

/**
 * 🏆 Achievements board (v7.99, per Javi: "lets just add achievements"): the
 * ledger's id list is the claim. The COUNT ranks; at a tie the row that reached the
 * count first wins, so the server folds the moment it first saw the count into the
 * score. Count dominates: a millisecond clock (about 1.8e12) can never outweigh one
 * achievement (1e13), and 35 achievements stay far inside a safe integer.
 */
export interface AchievementsClaim {
  ids: string[]
}

export const ACHIEVEMENTS_SCORE_BASE = 10_000_000_000_000

export function achievementsScore(count: number, reachedAt: number): number {
  return count * ACHIEVEMENTS_SCORE_BASE - reachedAt
}

export function achievementsClaimError(c: unknown): string | null {
  if (!c || typeof c !== 'object') return 'no claim'
  const d = c as Record<string, unknown>
  if (!Array.isArray(d.ids) || d.ids.length > ACHIEVEMENTS.length) return 'ids out of range'
  if (!d.ids.every(id => typeof id === 'string' && !!ACHIEVEMENT_BY_ID[id])) return 'unknown achievement'
  if (new Set(d.ids).size !== d.ids.length) return 'duplicate achievement'
  return null
}
